import { useState } from 'react';
import { HelpDrawer } from './ui/HelpDrawer';

const API_KEY_SNIPPET = `// Authenticate server-side requests with your API key
fetch('https://api.prelaunchwaitlist.com/v1/subscribers', {
  headers: {
    'x-api-key': process.env.PLW_API_KEY,
    'Content-Type': 'application/json',
  },
});`;

function ApiKeyHelp() {
  return (
    <div>
      <h3 className="text-sm font-bold text-gray-800 mb-1">API keys — coming soon</h3>
      <p className="text-sm text-gray-600 mb-4">
        API keys will let you authenticate server-side requests to the PreLaunch Waitlist REST API
        without a user session — perfect for CI pipelines, cron jobs, and backend integrations.
      </p>

      <h3 className="text-sm font-bold text-gray-800 mb-1">How it will work</h3>
      <p className="text-sm text-gray-600 mb-3">
        Generate a key from this dashboard, then pass it as the{' '}
        <code className="bg-gray-100 px-1 rounded">x-api-key</code> header on any API request.
      </p>
      <pre className="bg-slate-800 text-slate-200 p-4 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap break-all mb-5">
        {API_KEY_SNIPPET}
      </pre>

      <ul className="text-sm text-gray-600 space-y-1.5 pl-4 list-disc">
        <li>Each key is scoped to a single project.</li>
        <li>Revoke keys at any time \u2014 old keys stop working immediately.</li>
        <li>Store keys in environment variables, never in source code.</li>
      </ul>
    </div>
  );
}

interface ApiKeyManagerProps {
  projectId: string;
}

export function ApiKeyManager(_: ApiKeyManagerProps) {
  const [helpOpen, setHelpOpen] = useState(false);

  return (
    <>
      <section className="relative bg-white rounded-xl p-6 mb-6 shadow-sm overflow-hidden">
        {/* Coming Soon overlay */}
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-xl bg-white/80 backdrop-blur-sm">
          <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-brand-500 text-white text-xs font-bold uppercase tracking-widest shadow mb-2">
            🚀 Coming soon
          </span>
          <p className="text-sm text-gray-500 text-center max-w-xs">
            API keys are on the way. You&apos;ll be able to authenticate server-side requests
            directly without a user session.
          </p>
        </div>

        {/* Section header */}
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-base font-bold m-0">API Keys</h3>
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            className="bg-transparent border border-gray-300 cursor-pointer text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-brand-500 hover:border-brand-400 transition-colors"
            aria-label="Open API key help"
          >
            ?
          </button>
        </div>

        {/* Placeholder description */}
        <p className="text-sm text-gray-500 mb-4">
          Generate API keys to authenticate server-side requests to the PreLaunch Waitlist API on
          behalf of this project.
        </p>

        {/* Placeholder create form */}
        <div className="flex gap-2 mb-4 flex-wrap">
          <input
            type="text"
            className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-md text-sm outline-none"
            placeholder="Key name (e.g. production-server)"
            disabled
          />
          <button
            type="button"
            className="px-4 py-2 bg-brand-500 text-white border-0 rounded-md text-sm font-semibold whitespace-nowrap opacity-60 cursor-not-allowed"
            disabled
          >
            Create Key
          </button>
        </div>

        {/* Placeholder key list */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className="text-left px-3 py-2 border-b-2 border-gray-200 text-gray-700 font-semibold">
                  Name
                </th>
                <th className="text-left px-3 py-2 border-b-2 border-gray-200 text-gray-700 font-semibold">
                  Key
                </th>
                <th className="text-left px-3 py-2 border-b-2 border-gray-200 text-gray-700 font-semibold">
                  Created
                </th>
                <th className="px-3 py-2 border-b-2 border-gray-200"></th>
              </tr>
            </thead>
            <tbody>
              <tr className="hover:bg-gray-50">
                <td className="px-3 py-2 border-b border-gray-100 text-gray-600">
                  production-server
                </td>
                <td className="px-3 py-2 border-b border-gray-100">
                  <code className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                    arg_live_••••••••••••••••
                  </code>
                </td>
                <td
                  className="px-3 py-2 border-b border-gray-100 text-gray-600"
                  aria-label="Creation date not available"
                >
                  —
                </td>
                <td className="px-3 py-2 border-b border-gray-100">
                  <button
                    type="button"
                    className="px-2 py-1 text-xs font-semibold border border-red-400 rounded-md bg-transparent text-red-500 opacity-50 cursor-not-allowed"
                    disabled
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
      <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} title="API keys">
        <ApiKeyHelp />
      </HelpDrawer>
    </>
  );
}
